import dayjs from "dayjs"
import PropTypes from "prop-types"
import { useCallback, useMemo } from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import Styles from "../BarStatistics.module.scss"
import { monthNames } from "../lib"

/**
 * Participants
 * Renders a stacked bar chart of participants by either:
 * - month view (12 months for the given year), or
 * - day view (each day for the given month of the given year)
 */
const Participants = ({ month, statisticsData, year, getText }) => {
  // Localized series labels, stable per render
  const labels = useMemo(
    () => ({
      registered: getText("08.ST.0500.L.27"), // Angemeldet
      added: getText("08.ST.0500.L.28"), // Hinzugefügt
      noShow: getText("08.ST.0500.L.29"), // Nicht erschienen
    }),
    [getText]
  )

  // Helper to sum a set of gender fields safely
  const sumFields = useCallback(
    (stats, keys) => keys.reduce((acc, k) => acc + (stats[k] || 0), 0),
    []
  )

  // Compute all core sums from one stats record once for clarity
  const computeSums = useCallback(
    (stats) => {
      const registeredWithResult = sumFields(stats, [
        "gender_d_registered_with_result",
        "gender_m_registered_with_result",
        "gender_f_registered_with_result",
      ])

      const registeredWithoutResult = sumFields(stats, [
        "gender_d_registered_without_result",
        "gender_m_registered_without_result",
        "gender_f_registered_without_result",
      ])

      const registeredLaterWithResult = sumFields(stats, [
        "gender_d_registered_later_with_result",
        "gender_m_registered_later_with_result",
        "gender_f_registered_later_with_result",
      ])

      const registeredLaterWithoutResult = sumFields(stats, [
        "gender_d_registered_later_without_result",
        "gender_m_registered_later_without_result",
        "gender_f_registered_later_without_result",
      ])

      const registeredDeletedAtDayWithoutResult = sumFields(stats, [
        "gender_d_registered_deletedatday_withoutresult",
        "gender_m_registered_deletedatday_withoutresult",
        "gender_f_registered_deletedatday_withoutresult",
      ])

      return {
        registeredWithResult,
        registeredWithoutResult,
        registeredLaterWithResult,
        registeredLaterWithoutResult,
        registeredDeletedAtDayWithoutResult,
      }
    },
    [sumFields]
  )

  // Build empty scaffolds
  const makeEmptyDailyData = (monthIndex, y, labels) => {
    const date = dayjs().month(monthIndex).year(y)
    const out = []
    for (let i = 1; i <= date.daysInMonth(); i++) {
      out.push({
        name: i,
        [labels.registered]: 0,
        [labels.added]: 0,
        [labels.noShow]: 0,
      })
    }
    return out
  }

  const makeEmptyMonthlyData = (labels) =>
    monthNames.map((name) => ({
      name,
      [labels.registered]: 0,
      [labels.added]: 0,
      [labels.noShow]: 0,
    }))

  const data = useMemo(() => {
    if (month !== undefined) {
      // Daily view within the given month/year
      const rows = makeEmptyDailyData(month, year, labels)

      statisticsData?.statistics?.forEach((stats) => {
        if (!stats.participated) return

        const statsDate = dayjs(stats.visit_date)
        // Ensure same month/year
        if (statsDate.month() !== month || statsDate.year() !== year) return

        const dayIdx = statsDate.date() - 1
        if (dayIdx < 0 || dayIdx >= rows.length) return

        const s = computeSums(stats)

        // Angemeldet = with_result + without_result + deleted_at_day_without_result
        rows[dayIdx][labels.registered] += s.registeredWithResult
        rows[dayIdx][labels.registered] += s.registeredWithoutResult
        rows[dayIdx][labels.registered] += s.registeredDeletedAtDayWithoutResult

        // Hinzugefügt = later_with_result + later_without_result
        rows[dayIdx][labels.added] += s.registeredLaterWithResult
        rows[dayIdx][labels.added] += s.registeredLaterWithoutResult

        // Nicht erschienen = without_result + deleted_at_day_without_result
        rows[dayIdx][labels.noShow] += s.registeredWithoutResult
        rows[dayIdx][labels.noShow] += s.registeredDeletedAtDayWithoutResult
      })

      return rows
    }

    // Monthly view for the whole year
    const rows = makeEmptyMonthlyData(labels)

    statisticsData?.statistics?.forEach((stats) => {
      if (!stats.participated) return

      const statsDate = dayjs(stats.visit_date)
      if (statsDate.year() !== year) return

      const monthIdx = statsDate.month()

      const s = computeSums(stats)

      // Angemeldet
      rows[monthIdx][labels.registered] += s.registeredWithResult
      rows[monthIdx][labels.registered] += s.registeredWithoutResult
      rows[monthIdx][labels.registered] += s.registeredDeletedAtDayWithoutResult

      // Hinzugefügt
      rows[monthIdx][labels.added] += s.registeredLaterWithResult
      rows[monthIdx][labels.added] += s.registeredLaterWithoutResult

      // Nicht erschienen
      rows[monthIdx][labels.noShow] += s.registeredWithoutResult
      rows[monthIdx][labels.noShow] += s.registeredDeletedAtDayWithoutResult
    })

    return rows
  }, [month, labels, statisticsData?.statistics, year, computeSums])

  const labelFormatter = useCallback(
    (value) => {
      if (month == null) {
        // Monthly view: value is the month name as provided in data.name
        return value
      }
      // Daily view: value is the day number within the selected month/year
      return dayjs().month(month).year(year).date(value).format("DD.MM.YYYY")
    },
    [month, year]
  )

  return (
    <div>
      <ResponsiveContainer
        width="100%"
        height="100%"
        minHeight="500px"
        minWidth="500px"
      >
        <BarChart
          width={500}
          height={300}
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis
            domain={month != null ? [0, 96] : undefined}
            ticks={month != null ? [0, 32, 64, 96] : undefined}
          />
          <Tooltip labelFormatter={labelFormatter} />
          <Legend />
          <Bar
            dataKey={labels.registered}
            stackId="registered"
            fill="#fae760"
          />
          <Bar dataKey={labels.added} stackId="registered" fill="#93918a" />
          <Bar dataKey={labels.noShow} fill="#d5d4cf" />
        </BarChart>
      </ResponsiveContainer>
      <div className={Styles.description}>{getText("08.ST.0500.C.08")}</div>
    </div>
  )
}

Participants.propTypes = {
  statisticsData: PropTypes.object,
  month: PropTypes.number,
  year: PropTypes.number.isRequired,
}

export { Participants }
